import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  TableContainer,
  TableContent,
  CELL_RENDERERS,
  TableHeaderCustomContent,
  INPUT_SIZES,
  TablePagination,
  DEFAULT_ROWS_PER_PAGE_OPTIONS,
  TextButton,
  usePreventTableScrollingRef,
  CHECKBOX_SIZES,
  TextRenderer,
  focusLastCell,
  preventDefaultBehavior,
  SearchInput,
  Checkbox,
} from 'cdk-radial';
import { AgGridReact } from 'ag-grid-react';
import classNames from 'classnames';
import '../../styles/PrivacyProtection.scss';
import 'ag-grid-community/dist/styles/ag-grid.css';
import 'ag-grid-community/dist/styles/ag-theme-balham.css';
import { CONSTANTS } from '../../constants';
import {
  requestedIntegrationCppStatusChange,
  selectAllLocationsForCpp,
} from '../../../../../../../redux/connectedComponent/ManageIntegrationsV2/ManageIntegrationsV2.slice';

export const PrivacyProtectionCheckBoxRenderer = ({ data = {} }) => {
  const dispatch = useDispatch();
  const { requestedCppStatuses } = useSelector(
    (state) => state.ManageIntegrationsV2Slice
  );
  const { entityId, index } = data;

  const handleCheckBoxChecked = useCallback((e, entityId) => {
    const isChecked = e.target.checked;
    dispatch(requestedIntegrationCppStatusChange({ entityId, isChecked }));
  }, []);

  return (
    <Checkbox
      data-testid={`dealership-location-checkbox-${index}`}
      key={index}
      size={CHECKBOX_SIZES.LARGE}
      onChange={(e) => handleCheckBoxChecked(e, entityId)}
      checked={requestedCppStatuses[entityId] ? true : false}
    />
  );
};

export const SelectAllLocationsForPrivacyProtection = (rowData) => {
  const dispatch = useDispatch();

  const handleSelectAllLocationsForCpp = useCallback(() => {
    dispatch(selectAllLocationsForCpp({ allLocations: rowData.rowData }));
  }, []);
  return (
    <div>
      {CONSTANTS.PRIVACY_PROTECTION_HEADER_NAME}
      <TextButton
        text={'Select All'}
        onClick={handleSelectAllLocationsForCpp}
      />
    </div>
  );
};

export const PrivacyProtectionTable = ({ apiName }) => {
  const gridRef = useRef();

  const { entityIdNameMapping, entityIdAddressMapping } = useSelector(
    (state) => state.loginDetailsSlice
  );

  const { requestedIntegrationStatuses } = useSelector(
    (state) => state.ManageIntegrationsV2Slice
  );

  const [gridApi, setGridApi] = useState(null);
  const [currentPage, setCurrentPage] = useState();
  const [totalPages, setTotalPages] = useState();
  const [rowData, setRowData] = useState([]);
  const [value, setValue] = useState('');
  const [totalRows, setTotalRows] = useState();
  const tableContentRef = usePreventTableScrollingRef();
  const isAgGridReady = !!gridApi;
  const defaultPaginationPageSize = DEFAULT_ROWS_PER_PAGE_OPTIONS[0];
  const rowsPerPageOptions = DEFAULT_ROWS_PER_PAGE_OPTIONS;

  const frameworkComponentsExtended = {
    textRenderer: TextRenderer,
  };

  const componentMainClass = 'privacy_protection_table';
  const agGridThemeClass = 'ag-theme-balham';
  const tableClass = classNames(componentMainClass, agGridThemeClass);

  const columns = [
    {
      cellRenderer: CELL_RENDERERS.TEXT,
      field: 'location',
      headerName: CONSTANTS.DEALERSHIP_LOCATION
    },
    {
      cellRenderer: CELL_RENDERERS.TEXT,
      field: 'address',
      headerName: CONSTANTS.ADDRESS
    },
    {
      cellRendererFramework: PrivacyProtectionCheckBoxRenderer,
      field: 'location',
      headerComponentFramework: SelectAllLocationsForPrivacyProtection,
      headerComponentParams: {
        rowData: rowData,
      },
    },
  ];

  const handleSearchInputChange = (searchInput) => {
    setValue(searchInput);

    if (gridApi) {
      gridApi.setQuickFilter(searchInput); // Refresh is needed for filtered cells highlighting to work properly

      gridApi.refreshCells({
        force: true,
      });
      const displayedRowsCount = gridApi.getDisplayedRowCount();
      setTotalRows(displayedRowsCount);

      if (searchInput && displayedRowsCount === 0) {
        gridApi.showNoRowsOverlay();
      } else {
        gridApi.hideOverlay();
      }
    }
  };

  const handleGridReady = (gridParams) => {
    setGridApi(gridParams.api);
    setCurrentPage(gridParams.api.paginationGetCurrentPage() + 1);
    setTotalPages(gridParams.api.paginationGetTotalPages());
    setTotalRows(gridParams.api.getDisplayedRowCount());
    gridParams.api.sizeColumnsToFit();
  };

  const handleTabbingFromPagination = (event) => {
    const hasRowsToDisplay = gridApi.rowModel.rowsToDisplay.length > 0;
    if (hasRowsToDisplay) {
      preventDefaultBehavior(event);
      focusLastCell(gridApi);
    }
  };

  const paginationPageSize = () => {
    if (isAgGridReady) {
      return gridApi.paginationGetPageSize();
    }

    if (
      defaultPaginationPageSize.value === DEFAULT_ROWS_PER_PAGE_OPTIONS[0].value
    ) {
      return rowsPerPageOptions[0].value;
    }

    if (
      rowsPerPageOptions.map(
        (rowPerPageOption) =>
          rowPerPageOption === defaultPaginationPageSize.value
      )
    ) {
      return defaultPaginationPageSize.value;
    }
  };

  const getPageSummaryText = (currentPage, totalPages) => {
    return `Page ${currentPage} of ${totalPages}`;
  };

  const getRowsSummaryText = (currentPage, pageSize, totalRows) => {
    if (totalRows === 0) {
      return `${totalRows}-${totalRows} of ${totalRows}`;
    }
    const rowsFrom = (currentPage - 1) * pageSize + 1;
    const rowsTo = currentPage * pageSize;
    return `${rowsFrom}-${
      rowsTo > totalRows ? totalRows : rowsTo
    } of ${totalRows}`;
  };

  const handlePaginationChanged = () => {
    if (!isAgGridReady) return;
    setCurrentPage(gridApi.paginationGetCurrentPage() + 1);
    setTotalPages(gridApi.paginationGetTotalPages());
    setTotalRows(gridApi.getDisplayedRowCount());
  };

  const paginationProps = {
    ariaLabel: 'Pagination',
    buttonLabels: {
      goToFirst: 'Go to first',
      goToLast: 'Go to last',
      goToNext: 'Go to next',
      goToPrevious: 'Go to previous',
    },
    className: '',
    dataTestId: `purchase-history-table-pagination`,
    defaultPaginationPageSize: DEFAULT_ROWS_PER_PAGE_OPTIONS[0],
    gridApi,
    hideBorder: false,
    onTabbingIntoTable: handleTabbingFromPagination,
    pageSizeDropdownId: `purchase-history-page-size-dropdown-table`,
    pageSummaryLabelId: `purchase-history-pagination-summary-label-table`,
    pageSummaryText: getPageSummaryText(
      totalPages === 0 ? 0 : currentPage,
      totalPages
    ),
    rowsPerPageLabel: 'Rows per page:',
    rowsPerPageOptions: DEFAULT_ROWS_PER_PAGE_OPTIONS,
    rowsSummaryText: getRowsSummaryText(
      currentPage,
      paginationPageSize(),
      totalRows
    ),
    showGoToFirstLastPage: true,
  };
  useEffect(() => {
    let requestedLocations = [];
    for (const entityId of Object.keys(requestedIntegrationStatuses)) {
      if (requestedIntegrationStatuses[entityId] === 'enable') {
        requestedLocations.push({
          location: entityIdNameMapping[entityId],
          address: entityIdAddressMapping[entityId],
          entityId,
        });
      }
    }
    setRowData(requestedLocations);
  }, [requestedIntegrationStatuses]);

  return (
    <TableContainer className={tableClass}>
      <TableHeaderCustomContent
        className="custom-header"
        customHeaderContent={
          <div className="privacy-protection-header-container">
            <h3 className="privacy-protection-header-text">
              Consumer Privacy Protection ({apiName})
            </h3>
            <SearchInput
              clearButtonLabel="clearButtonLabel"
              id="search-input-expanded"
              isAlwaysExpanded
              name="search-input-expanded"
              onChange={handleSearchInputChange}
              placeholder="Search Dealership"
              size={INPUT_SIZES.STANDARD}
              value={value}
            />
          </div>
        }
      />
      <TableContent ref={tableContentRef}>
        <AgGridReact
          columnDefs={columns}
          rowData={rowData}
          frameworkComponents={frameworkComponentsExtended}
          suppressRowHoverHighlight={true}
          rowStyle={{ background: 'white' }}
          onGridReady={handleGridReady}
          onPaginationChanged={handlePaginationChanged}
          pagination={true}
          paginationPageSize={paginationPageSize()}
          paginationAutoPageSize={false}
          suppressPaginationPanel
          ref={gridRef}
          rowHeight={32}
          domLayout="autoHeight"
        />
      </TableContent>
      {isAgGridReady && (
        <TablePagination
          defaultPaginationPageSize={defaultPaginationPageSize}
          {...paginationProps}
        />
      )}
    </TableContainer>
  );
};
